import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TenantResponse } from './tenant.model';
import { LogService } from '../shared/services/log.service';
import { GlobalService } from '../shared/services/global.service';
import { SimpleTenant } from '../shared/models/simple-tenant.model';
import { ChurchThemeService } from '../shared/services/church-theme.service';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  apiHost: string = environment.apiHost;

  constructor(
    private http: HttpClient,
    private logService: LogService,
    private globalService: GlobalService,
    private churchThemeService: ChurchThemeService
  ) { }

  public tenantNamesCache: string[] | null = null;  


  getTenantForString(s: string) {
    return s;
  }

  getTenantForHost(host: string): string {
    return this.getTenantForString(host);
  }

getTenant(): string {
  const host = window.location.hostname;
  const path = window.location.pathname.toLowerCase();

  // =============================
  // 1. PATH-BASED (PRIMARY)
  // =============================
  const pathTenant = path.split('/')[1];

  const nonTenantRoutes = [
    '',
    'home',
    'login',
    'signup',
    'resetpassword',
    '404'
  ];

  // ✅ only return if VALID tenant from DB cache
  if (
    pathTenant &&
    !nonTenantRoutes.includes(pathTenant) &&
    this.tenantNamesCache?.includes(pathTenant)
  ) {
    return pathTenant;
  }

  // =============================
  // 2. SUBDOMAIN (LEGACY SUPPORT)
  // =============================
  if (host === 'localhost' || host.endsWith('.localhost')) {
    const parts = host.split('.');
    const sub = parts.length > 1 ? parts[0] : null;

    // ✅ validate subdomain against cache
    if (sub && this.tenantNamesCache?.includes(sub)) {
      return sub;
    }

    return 'main';
  }

  const allowedDomains = [
    'church-services.org',
    'church-services-staging.org'
  ];

  const matchedDomain = allowedDomains.find(d => host.endsWith(d));

  if (matchedDomain) {
    const parts = host.split('.');
    if (parts.length > 2) {
      const sub = parts[0];

      // ✅ validate subdomain
      if (this.tenantNamesCache?.includes(sub)) {
        return sub;
      }
    }
  }

  // =============================
  // 3. DEFAULT
  // =============================
  return 'main';
}

addTenantToHeaders(headers: HttpHeaders): HttpHeaders {
  let tenant = this.getTenant();

  // handle empty, null, undefined
  if (!tenant) {
    tenant = localStorage.getItem('tenant') || 'main';
  }

  this.globalService.currentTenant = tenant;

  return headers.append('X-Tenant-ID', tenant).set('X-Language', this.globalService.currentLanguage || 'en');
}

  async getLanguage() {
    if (
      this.globalService.currentLanguage != null &&
      this.globalService.currentLanguage != '' &&
      this.globalService.currentLanguage != '0'
    )
      return this.globalService.currentLanguage;
    var tenant = await this.getSimpleTenant();
    return tenant.language;
  }

  getDioceseTenants(): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetDioceseTenants?dioceseSubdomain=` + this.getTenant();
      this.http
        .post<TenantResponse>(apiURL, null)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetTenantResponse',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  getTenantData(): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetTenant?subDomain=` + this.getTenant();
      this.http
        .get<TenantResponse>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetTenantResponse',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  getSimpleTenant(forceRefresh = false): Promise<SimpleTenant> {
    const requestedTenant = this.getTenant();
    let promise = new Promise<SimpleTenant>((resolve, reject) => {
      if (!forceRefresh && this.globalService.subdomain == requestedTenant && this.globalService.simpleTenant != null)
      {
        this.churchThemeService.applyTheme(this.globalService.simpleTenant.colorTheme, this.getTenant(), true);
        return resolve(this.globalService.simpleTenant);
      }
      let apiURL =
        `${this.apiHost}/Tenant/GetSimpleTenantData?subDomain=` + requestedTenant;
      this.http
        .get<TenantResponse>(apiURL)
        .toPromise()
        .then(
          (res) => {
            if (this.getTenant() === requestedTenant) {
              this.globalService.subdomain = requestedTenant;
              this.globalService.simpleTenant = res['tenant'];
            }
            this.churchThemeService.applyTheme(res['tenant'].colorTheme, requestedTenant, true);
            resolve(res['tenant']);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Get Simple Tenant',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  getTenantById(tenantId: number): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetTenantById?tenantId=` + tenantId;
      this.http
        .get<TenantResponse>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetTenantById',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

setTenantNames(names: string[]) {
  this.tenantNamesCache = names.map(t => t.toLowerCase());
}  

  getAllTenants(): Promise<TenantResponse[]> {
    let promise = new Promise<TenantResponse[]>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetAllTenants`;
      this.http
        .get<TenantResponse[]>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetAllTenants',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  getAllTenantNames(): Promise<string[]> {
    let promise = new Promise<string[]>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/GetAllTenantNames`;
      this.http
        .get<string[]>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: GetAllTenantNames',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  addTenant(tenant: TenantResponse): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/Tenant/addTenant`;
      this.http
        .post<TenantResponse>(apiURL, tenant)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Add Tenant',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  updateTenant(tenant: TenantResponse, checkNewSecrets: boolean): Promise<TenantResponse> {
    let promise = new Promise<TenantResponse>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/tenant/UpdateTenant?RefreshSecrets=` + checkNewSecrets;
      this.http
        .post<TenantResponse>(apiURL, tenant)
        .toPromise()
        .then(
          (res) => {
            // Reload public settings after an administrator saves this church.
            if (this.globalService.simpleTenant?.id === tenant.id || this.globalService.subdomain === tenant.subdomain) {
              this.globalService.simpleTenant = null;
            }
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Update Tenant',
                msg
              );
            reject('Please check your connection!');
          }
        );
    });
    return promise;
  }

  getTenantLogo(subdomain: string): Promise<string> {
    let promise = new Promise<string>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/file/GetLogo?subdomain=` + subdomain;
      this.http
        .get<string>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res['imageUrl']);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Get Tenant Logo',
                msg
              );
            reject('');
          }
        );
    });
    return promise;
  }

  tenantExists(subdomain: string): Promise<boolean> {
    let promise = new Promise<boolean>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/tenant/TenantExists?subdomain=` + subdomain;
      this.http
        .get<boolean>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Tenant Exists failing',
                msg
              );
            reject('');
          }
        );
    });
    return promise;
  }

  getTenantSmallLogo(subdomain: string): Promise<string> {
    let promise = new Promise<string>((resolve, reject) => {
      let apiURL =
        `${this.apiHost}/file/GetSmallLogo?subdomain=` + subdomain;
      this.http
        .get<string>(apiURL)
        .toPromise()
        .then(
          (res) => {
            resolve(res['imageUrl']);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog(
                'Tenant Service: Get Tenant Small Logo',
                msg
              );
            reject('');
          }
        );
    });
    return promise;
  }
}
